import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { makeStyles } from "@material-ui/styles";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import { schema } from "../../utils/getSchemaAdForm";
import { formAdFields, GCP_BUCKET_URL_AVATARS } from "../../constants/data";

import { TITLE_BUTTONS } from "../../constants/titles";

import TextForm from "../../components/FormFields/TextForm";
import SelectCheckMarks from "../../components/FormFields/SelectCheckMarks";
import AutocompleteField from "../../components/FormFields/AutocompleteField";
import CountrySelect from "../../components/FormFields/CountrySelect";
import TeamSearchField, { toTeamOption } from "../TeamSearchField";
import { GET_TEAM } from "../../constants/endpoints";
import { buildApiUrl, requestJson } from "../../dataProvider/requestJson";

const useStyles = makeStyles(() => ({
  fieldForm: { width: "450px" },
  sectionTitle: { marginTop: "8px" },
  previewWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",
  },
  previewImg: {
    maxWidth: "500px",
    width: "100%",
    maxHeight: "240px",
    objectFit: "contain",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 4,
  },
  helper: { fontSize: "12px", opacity: 0.75, textAlign: "center" },
  imageActions: { display: "flex", gap: "10px", justifyContent: "center" },
  submitRow: { display: "flex", gap: "10px", justifyContent: "center" },
  hiddenInput: { display: "none" },
}));

const STATUS_OPTIONS = [
  { id: "DRAFT", label: "DRAFT" },
  { id: "ACTIVE", label: "ACTIVE" },
  { id: "PAUSED", label: "PAUSED" },
  { id: "ENDED", label: "ENDED" },
];

const AD_TYPE_OPTIONS = [
  { id: "CAMP", label: "CAMP" },
  { id: "CLINIC", label: "CLINIC" },
  { id: "JOB", label: "JOB" },
  { id: "SERVICE", label: "SERVICE" },
  { id: "RETAIL", label: "RETAIL" },
  { id: "MEET", label: "MEET" },
];

// Must match SelectCheckMarks rendering (it uses `abbreviation`) and selection identity (`id`)
const TARGET_ROLE_OPTIONS = [
  { id: "DIVER", abbreviation: "DIVER", name: "DIVER" },
  { id: "PARENT", abbreviation: "PARENT", name: "PARENT" },
  { id: "DIVING_COACH", abbreviation: "DIVING_COACH", name: "DIVING_COACH" },
  {
    id: "SWIMMING_COACH",
    abbreviation: "SWIMMING_COACH",
    name: "SWIMMING_COACH",
  },
  {
    id: "HEAD_DIVING_COACH",
    abbreviation: "HEAD_DIVING_COACH",
    name: "HEAD_DIVING_COACH",
  },
  { id: "TEAM_ADMIN", abbreviation: "TEAM_ADMIN", name: "TEAM_ADMIN" },
  { id: "SWIMMER", abbreviation: "SWIMMER", name: "SWIMMER" },
  { id: "COACH", abbreviation: "COACH", name: "COACH" },
];

const toNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const isoToDatetimeLocal = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

const datetimeLocalToIso = (val) => {
  if (!val) return null;
  try {
    return new Date(val).toISOString();
  } catch {
    return null;
  }
};
const extractApiErrorMessage = (err) => {
  // Defensive: handle axios/fetch/custom errors without throwing while formatting.
  try {
    if (!err) return "Save failed. Please try again.";

    // axios-style
    const axiosMsg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data;
    if (typeof axiosMsg === "string" && axiosMsg.trim()) return axiosMsg.trim();

    // fetch-style "Response" body already read by caller sometimes; just use status text
    if (err?.status && (err?.statusText || err?.message)) {
      return `${err.status} ${err.statusText || err.message}`.trim();
    }

    // generic
    if (typeof err?.message === "string" && err.message.trim())
      return err.message.trim();

    // last resort
    const s = String(err);
    return s && s !== "[object Object]" ? s : "Save failed. Please try again.";
  } catch {
    return "Save failed. Please try again.";
  }
};

const flattenRhfErrors = (errors) => {
  // react-hook-form errors can be nested; collect messages
  const out = [];
  const walk = (obj, path = []) => {
    if (!obj) return;
    for (const [k, v] of Object.entries(obj)) {
      if (!v) continue;
      if (v.message)
        out.push({ path: [...path, k].join("."), message: v.message });
      else if (typeof v === "object") walk(v, [...path, k]);
    }
  };
  walk(errors);
  return out;
};

const ensureMaster = (k) => (/_master$/i.test(k) ? k : `${k}_master`);

const resolvePreviewFromObjectKey = (imageObjectKey) => {
  const key = toNull(imageObjectKey);
  if (!key) return "";
  const normalized = key.replace(/^\/+/, "");
  return `${GCP_BUCKET_URL_AVATARS}${ensureMaster(normalized)}`;
};

// Defensive: handle camelCase + snake_case coming from backend/DTO mapping
const pickField = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    const n = toNull(v);
    if (n != null) return n;
  }
  return null;
};

const normalizeTargetRolesForSelect = (rawRoles) => {
  // rawRoles may be: ["DIVER","PARENT"] OR [{id,abbreviation,...}] OR null
  if (!Array.isArray(rawRoles) || rawRoles.length === 0) return [];

  // if already objects (edit payload might return objects someday), keep as-is
  const first = rawRoles[0];
  if (first && typeof first === "object") return rawRoles;

  // else assume array of strings and map to option objects
  const set = new Set(rawRoles.map((r) => toNull(r)).filter(Boolean));
  return TARGET_ROLE_OPTIONS.filter((opt) => set.has(opt.id));
};

const normalizeTargetRolesForSubmit = (rawRoles) => {
  // SelectCheckMarks returns objects; backend expects strings
  if (!Array.isArray(rawRoles) || rawRoles.length === 0) return [];

  return rawRoles
    .map((r) => {
      if (typeof r === "string") return toNull(r);
      return toNull(r?.id) || toNull(r?.abbreviation);
    })
    .filter(Boolean);
};

/**
 * Priority rules (frontend):
 * - default to 10
 * - required
 * - integer
 * - 1..99 inclusive (aka >0 and <100)
 */
const PRIORITY_DEFAULT = 10;
const PRIORITY_MIN = 1;
const PRIORITY_MAX = 99;

const normalizePriority = (value) => {
  // Accept number or string
  const raw = value == null || value === "" ? PRIORITY_DEFAULT : value;

  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) return PRIORITY_DEFAULT;

  const intVal = Math.trunc(n);

  if (intVal < PRIORITY_MIN) return PRIORITY_MIN;
  if (intVal > PRIORITY_MAX) return PRIORITY_MAX;
  return intVal;
};

const validatePriority = (value) => {
  if (value == null || value === "") return "Priority is required";

  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return "Priority must be a number";

  if (!Number.isInteger(n)) return "Priority must be an integer";
  if (n <= 0) return "Priority must be greater than 0";
  if (n >= 100) return "Priority must be less than 100";

  return true;
};

const AdCreateForm = ({
  handleClose,
  onSubmit,
  defaultValue,
  governingBodiesTitle,
}) => {
  const classes = useStyles();

  const [localPreview, setLocalPreview] = useState("");

  // The ad stores only teamId, so the name is looked up from the team itself.
  const initialTeamId = Number(defaultValue?.teamId);
  const [teamOption, setTeamOption] = useState(() =>
    Number.isFinite(initialTeamId) && initialTeamId > 0
      ? { id: initialTeamId, label: `Team #${initialTeamId}` }
      : null,
  );

  useEffect(() => {
    if (!Number.isFinite(initialTeamId) || initialTeamId <= 0) return undefined;
    let cancelled = false;
    requestJson(buildApiUrl(GET_TEAM(initialTeamId)))
      .then((team) => {
        const option = toTeamOption(team);
        if (!cancelled && option) {
          // Only fill in the name if the admin hasn't already picked another team.
          setTeamOption((prev) => (prev?.id === option.id ? option : prev));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [initialTeamId]);

  // Normalize incoming DTO fields
  const initialImageObjectKey = pickField(
    defaultValue,
    "imageObjectKey",
    "image_object_key",
  );
  const initialImageUrl = pickField(defaultValue, "imageUrl", "image_url");

  // If backend gave us a relative object key in imageUrl (e.g. "ads/ad_12.png"),
  // move it to imageObjectKey so Yup doesn't reject it as "not url/base64".
  let normalizedImageObjectKey = initialImageObjectKey;
  let normalizedImageUrl = initialImageUrl;

  if (typeof normalizedImageUrl === "string") {
    const s = normalizedImageUrl.trim();
    const isHttp = /^https?:\/\//i.test(s);
    const looksLikeBase64 =
      /^[A-Za-z0-9+/=]+$/.test(s) || s.startsWith("data:");

    if (s && !isHttp && !looksLikeBase64) {
      normalizedImageObjectKey = normalizedImageObjectKey || s;
      normalizedImageUrl = null;
    }
  }

  const initialTargetRoles = normalizeTargetRolesForSelect(
    defaultValue?.targetRoles,
  );

  // IMPORTANT: if existing DTO has 0/null/"" for priority, treat it as default (10)
  const initialPriority = useMemo(() => {
    const p = defaultValue?.priority;
    if (p == null || p === "") return PRIORITY_DEFAULT;
    const n = typeof p === "number" ? p : Number(String(p).trim());
    if (!Number.isFinite(n)) return PRIORITY_DEFAULT;
    if (n <= 0) return PRIORITY_DEFAULT;
    if (!Number.isInteger(n)) return normalizePriority(n);
    return normalizePriority(n);
  }, [defaultValue]);

  const { control, handleSubmit, watch, setValue, getValues } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValue,

      imageObjectKey: normalizedImageObjectKey,
      imageUrl: normalizedImageUrl,

      targetRoles: initialTargetRoles,

      status: defaultValue?.status ?? "ACTIVE",

      // CHANGE: default priority is 10 and never 0
      priority: initialPriority,

      governingBodies: Array.isArray(defaultValue?.governingBodies)
        ? defaultValue.governingBodies
        : [],

      logoChanged: false,
    },
    mode: "onBlur", // show errors when leaving field
    reValidateMode: "onChange",
  });

  const status = watch(formAdFields.status.short) || defaultValue?.status;
  const statusValue = STATUS_OPTIONS.find((x) => x.id === status) || null;

  const adType = watch(formAdFields.adType.short) || defaultValue?.adType;
  const adTypeValue = AD_TYPE_OPTIONS.find((x) => x.id === adType) || null;

  const imageObjectKey =
    watch(formAdFields.imageObjectKey.short) ?? initialImageObjectKey;
  const imageUrlValue = watch(formAdFields.imageUrl.short) ?? initialImageUrl;

  const previewUrl = useMemo(() => {
    if (localPreview) return localPreview;

    const direct = toNull(imageUrlValue);
    if (direct && direct.startsWith("http")) return direct;

    return resolvePreviewFromObjectKey(imageObjectKey);
  }, [localPreview, imageObjectKey, imageUrlValue]);

  const onInvalid = (errors) => {
    // eslint-disable-next-line no-console
    console.error(
      "AdCreateForm validation blocked submit:",
      errors,
      "values:",
      getValues(),
    );

    const items = flattenRhfErrors(errors);

    // Show top few so it’s not spammy
    const msg =
      items.length > 0
        ? items
            .slice(0, 4)
            .map((x) => `• ${x.message}`)
            .join("\n")
        : "Some required fields are missing or invalid.";

    toast.error(`Please fix these before saving:\n${msg}`, { autoClose: 8000 });
  };


  const doSubmit = handleSubmit(async (values) => {
    // Defensive normalization before send to parent (even if validation somehow gets bypassed)
    const cleaned = {
      ...values,
      imageUrl: toNull(values.imageUrl),
      imageObjectKey: toNull(values.imageObjectKey),
      targetRoles: normalizeTargetRolesForSubmit(values.targetRoles),

      // CHANGE: enforce priority 1..99 and default 10
      priority: normalizePriority(values.priority),
    };

    // eslint-disable-next-line no-console
    console.log("AdCreateForm submitting values:", cleaned);

    await onSubmit(cleaned);
  }, onInvalid);

  const handleChooseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("Image too large. Keep it under 5MB.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setLocalPreview(dataUrl);

      const base64 = dataUrl.split(",").pop() || "";
      setValue(formAdFields.imageUrl.short, base64 || null, {
        shouldDirty: true,
      });

      setValue("logoChanged", true, { shouldDirty: true });
    };
    reader.onerror = () => toast.error("Failed to read image file.");
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleDeleteImage = () => {
    setLocalPreview("");
    setValue(formAdFields.imageUrl.short, null, { shouldDirty: true });
    setValue(formAdFields.imageObjectKey.short, null, { shouldDirty: true });
    setValue("logoChanged", true, { shouldDirty: true });
  };

  const hasAnyImage = Boolean(localPreview) || Boolean(previewUrl);

  return (
    <Box>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          doSubmit();
        }}
      >
        <Grid
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          {/* TEAM */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TeamSearchField
                  value={teamOption}
                  onChange={(option) => {
                    setTeamOption(option);
                    field.onChange(option ? option.id : "");
                  }}
                  onBlur={field.onBlur}
                  label={formAdFields.teamId.title}
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
              control={control}
              name={formAdFields.teamId.short}
              defaultValue={defaultValue?.[formAdFields.teamId.short] ?? ""}
            />
          </Grid>

          {/* AD TYPE */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field }) => (
                <AutocompleteField
                  label={formAdFields.adType.title}
                  size="small"
                  data={AD_TYPE_OPTIONS}
                  field={{
                    ...field,
                    value: adTypeValue,
                    onChange: (val) =>
                      setValue(formAdFields.adType.short, val?.id || null),
                  }}
                />
              )}
              control={control}
              name={formAdFields.adType.short}
              defaultValue={defaultValue?.[formAdFields.adType.short] ?? "MEET"}
            />
          </Grid>

          {/* AD NAME */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, required: true }}
                  label={formAdFields.name.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.name.short}
              defaultValue={defaultValue?.[formAdFields.name.short] || ""}
            />
          </Grid>

          {/* CLICK URL */}
          {/* CLICK URL */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, required: true }}
                  label={formAdFields.clickUrl.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.clickUrl.short}
              defaultValue={defaultValue?.[formAdFields.clickUrl.short] || ""}
            />
          </Grid>

          {/* IMAGE */}
          <Grid item md={12} className={classes.fieldForm}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Image
            </Typography>
          </Grid>

          <Grid item md={12} className={classes.fieldForm}>
            {previewUrl ? (
              <div className={classes.previewWrap}>
                <img
                  className={classes.previewImg}
                  src={previewUrl}
                  alt="ad preview"
                />
                <Typography className={classes.helper}>
                  Preview is rendered at ~500px wide. Object key:{" "}
                  <b>{toNull(imageObjectKey) || "(none)"}</b>
                </Typography>
              </div>
            ) : (
              <Typography className={classes.helper}>
                No image selected.
              </Typography>
            )}
          </Grid>

          <Grid item md={12} className={classes.fieldForm}>
            <div className={classes.imageActions}>
              <input
                className={classes.hiddenInput}
                id="ad-image-upload"
                type="file"
                accept="image/*"
                onChange={handleChooseFile}
              />
              <label htmlFor="ad-image-upload" style={{ width: "100%" }}>
                <Button fullWidth variant="contained" component="span">
                  Upload different image
                </Button>
              </label>

              <Button
                fullWidth
                variant="outlined"
                onClick={handleDeleteImage}
                disabled={!hasAnyImage}
              >
                {TITLE_BUTTONS.DELETE}
              </Button>
            </div>
          </Grid>

          {/* Hidden fields backend needs */}
          <Grid item md={12} className={classes.hiddenInput}>
            <Controller
              render={({ field }) => <input {...field} />}
              control={control}
              name={formAdFields.imageObjectKey.short}
              defaultValue={initialImageObjectKey}
            />
          </Grid>

          <Grid item md={12} className={classes.hiddenInput}>
            <Controller
              render={({ field }) => <input {...field} />}
              control={control}
              name={formAdFields.imageUrl.short}
              defaultValue={initialImageUrl}
            />
          </Grid>

          {/* PRIORITY */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              control={control}
              name={formAdFields.priority.short}
              defaultValue={initialPriority}
              rules={{
                validate: validatePriority,
              }}
              render={({ field, fieldState }) => (
                <TextForm
                  field={{
                    ...field,
                    required: true,
                    type: "number",
                    inputProps: {
                      min: PRIORITY_MIN,
                      max: PRIORITY_MAX,
                      step: 1,
                    },

                    // CHANGE: keep empty string as empty (so "required" error can appear)
                    // instead of forcing 0 (0 is explicitly invalid for you)
                    onChange: (e) => {
                      const raw = e?.target?.value;
                      if (raw === "") {
                        field.onChange("");
                        return;
                      }

                      // allow typing, but store number when possible
                      const n = Number(raw);
                      field.onChange(Number.isFinite(n) ? n : "");
                    },

                    // CHANGE: normalize/clamp when leaving the field
                    onBlur: () => {
                      const normalized = normalizePriority(field.value);
                      field.onChange(normalized);
                    },
                  }}
                  label={formAdFields.priority.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
            />
          </Grid>

          {/* STATUS */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field }) => (
                <AutocompleteField
                  label={formAdFields.status.title}
                  size="small"
                  data={STATUS_OPTIONS}
                  field={{
                    ...field,
                    value: statusValue,
                    onChange: (val) =>
                      setValue(formAdFields.status.short, val?.id || "PAUSED"),
                  }}
                />
              )}
              control={control}
              name={formAdFields.status.short}
              defaultValue={
                defaultValue?.[formAdFields.status.short] || "ACTIVE"
              }
            />
          </Grid>

          {/* Max unique impressions */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, type: "number", inputProps: { min: 0 } }}
                  label={formAdFields.maxUniqueImpressions.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.maxUniqueImpressions.short}
              defaultValue={
                defaultValue?.[formAdFields.maxUniqueImpressions.short] ?? ""
              }
            />
          </Grid>

          {/* Max unique clicks */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, type: "number", inputProps: { min: 0 } }}
                  label={formAdFields.maxUniqueClicks.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.maxUniqueClicks.short}
              defaultValue={
                defaultValue?.[formAdFields.maxUniqueClicks.short] ?? ""
              }
            />
          </Grid>

          {/* GOVERNING BODIES */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field }) => (
                <SelectCheckMarks
                  field={field}
                  label={formAdFields.governingBodies.title}
                  size={"small"}
                  options={governingBodiesTitle}
                />
              )}
              control={control}
              name={formAdFields.governingBodies.short}
              defaultValue={
                defaultValue?.[formAdFields.governingBodies.short] || []
              }
            />
          </Grid>

          {/* TARGET ROLES */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field }) => (
                <SelectCheckMarks
                  field={field}
                  label={formAdFields.targetRoles?.title ?? "Target roles"}
                  size="small"
                  options={TARGET_ROLE_OPTIONS}
                />
              )}
              control={control}
              name={formAdFields.targetRoles?.short ?? "targetRoles"}
              defaultValue={initialTargetRoles}
            />
          </Grid>

          {/* SHORT DESCRIPTION */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, multiline: true, rows: 4 }}
                  label={formAdFields.shortDescription.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.shortDescription.short}
              defaultValue={
                defaultValue?.[formAdFields.shortDescription.short] || ""
              }
            />
          </Grid>

          {/* Ages */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={field}
                  label={formAdFields.ages.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.ages.short}
              defaultValue={defaultValue?.[formAdFields.ages.short] || ""}
            />
          </Grid>

          {/* Levels */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={field}
                  label={formAdFields.levels.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.levels.short}
              defaultValue={defaultValue?.[formAdFields.levels.short] || ""}
            />
          </Grid>

          {/* CTA */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{ ...field, required: true }}
                  label={formAdFields.ctaText.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.ctaText.short}
              defaultValue={defaultValue?.[formAdFields.ctaText.short] || ""}
            />
          </Grid>

          {/* CITY */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={field}
                  label={formAdFields.city.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.city.short}
              defaultValue={defaultValue?.[formAdFields.city.short] || ""}
            />
          </Grid>

          {/* REGION */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={field}
                  label={formAdFields.region.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.region.short}
              defaultValue={defaultValue?.[formAdFields.region.short] || ""}
            />
          </Grid>

          {/* COUNTRY */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <CountrySelect
                  field={{
                    ...field,
                    onChange: (value) => field.onChange(value),
                  }}
                  label={formAdFields.country.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.country.short}
              defaultValue={defaultValue?.[formAdFields.country.short] || null}
            />
          </Grid>

          {/* START / END */}
          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{
                    ...field,
                    type: "datetime-local",
                    value: isoToDatetimeLocal(field.value),
                    onChange: (e) =>
                      field.onChange(datetimeLocalToIso(e.target.value)),
                  }}
                  label={formAdFields.startDate.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.startDate.short}
              defaultValue={
                defaultValue?.[formAdFields.startDate.short] ?? null
              }
            />
          </Grid>

          <Grid item md={12} className={classes.fieldForm}>
            <Controller
              render={({ field, fieldState }) => (
                <TextForm
                  field={{
                    ...field,
                    type: "datetime-local",
                    value: isoToDatetimeLocal(field.value),
                    onChange: (e) =>
                      field.onChange(datetimeLocalToIso(e.target.value)),
                  }}
                  label={formAdFields.endDate.title}
                  size="small"
                  fieldState={fieldState}
                />
              )}
              control={control}
              name={formAdFields.endDate.short}
              defaultValue={defaultValue?.[formAdFields.endDate.short] ?? null}
            />
          </Grid>

          {/* SUBMIT / CANCEL */}
          <Grid item md={12} className={classes.fieldForm}>
            <div className={classes.submitRow}>
              <Button variant="contained" onClick={doSubmit}>
                <Typography>{TITLE_BUTTONS.FINISH}</Typography>
              </Button>
              <Button variant="outlined" onClick={handleClose}>
                <Typography>{TITLE_BUTTONS.CANCEL}</Typography>
              </Button>
            </div>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

AdCreateForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultValue: PropTypes.object.isRequired,
  governingBodiesTitle: PropTypes.array.isRequired,
};

export default AdCreateForm;
